import { Select } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";

import { NotificationPosition } from "@superblocksteam/shared";

import {
  useGetAutoTopupConfigQuery,
  useManualTopupChargeMutation,
  useUpdateAutoTopupConfigMutation,
} from "store/slices/reduxApi/billing";
import {
  sendSuccessUINotification,
  sendWarningUINotification,
} from "utils/notification";

import {
  AutoTopUpFormFields,
  type AutoFormState,
  autoFormEqual,
  autoFormFromServer,
  formatUsd,
  isWholeNumber,
  validateAutoForm,
} from "./AutoTopUpForm";
import { MANUAL_TOPUP_CACHE_KEY } from "./AutoTopUpPanel";

const PRESET_AMOUNTS = [50, 100, 250, 500, 1000, 2500] as const;
const CUSTOM_OPTION = "custom" as const;
const MIN_CUSTOM = 20;
const MAX_CUSTOM = 9999;

type AmountOption = (typeof PRESET_AMOUNTS)[number] | typeof CUSTOM_OPTION;

type Props = {
  /** Called to return to the Plan view (back chevron + Cancel button). */
  onBack: () => void;
  /** Called when modal should fully close (× icon, post-charge success). */
  onClose: () => void;
};

/**
 * "Refill credits" body for the Manage Plan modal (formerly "Top up mode").
 *
 * Renders inside the existing modal frame (same `<Modal>` antd instance,
 * no nested modal — that flickers because the dim overlay doubles up).
 * Shows two stacked subsections, each with its own header + explainer:
 *
 *   1. Refill credits (top): one-time top-up — Amount select + pricing card
 *      with "Same rate" pill
 *   2. Auto refill (below): switch + 3-input grid with inline `= $X.XX`
 *      preview directly under each charge-driving input
 *
 * Save model — decoupled:
 *   - The bottom primary "Refill N credits" button commits the manual charge
 *     only. It never persists auto-refill preferences as a side effect.
 *   - Auto-refill settings are saved independently via the inline "Save
 *     Auto-refill settings" button next to the Auto-refill toggle, which
 *     appears when the form is dirty. Admins explicitly opt into persisting
 *     preference changes — no surprise side-effects from the charge button.
 *   - Visual hierarchy: only one black primary at a time. Inline Save uses the
 *     secondary white-with-shadow style so the bottom Refill stays the
 *     unambiguous primary CTA.
 */
export function TopUpView({ onBack, onClose }: Props) {
  const { data, isLoading } = useGetAutoTopupConfigQuery();
  const [manualCharge, { isLoading: isCharging }] =
    useManualTopupChargeMutation({ fixedCacheKey: MANUAL_TOPUP_CACHE_KEY });
  const [updateAutoConfig, { isLoading: isSavingAuto }] =
    useUpdateAutoTopupConfigMutation();

  const [selected, setSelected] = useState<AmountOption>(100);
  const [customRaw, setCustomRaw] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [chargeError, setChargeError] = useState<string | null>(null);
  // Timestamp of the most recent auto-refill PATCH success. Drives the
  // "✓ Saved" green chip swap-in and auto-clears 2s later so the slot
  // reverts to "hidden when clean". Tied to a timestamp (not a boolean)
  // so rapid re-saves restart the timer without leaking a stale clear.
  const [autoSavedAt, setAutoSavedAt] = useState<number | null>(null);

  const [autoForm, setAutoForm] = useState<AutoFormState>({
    enabled: false,
    packCredits: "",
    monthlyCreditCap: "",
    triggerBufferCredits: "",
  });

  // Sync auto form from server every time `data` changes (mount + cache
  // refresh). Discards in-flight edits — acceptable since the server-side
  // change-detection (e.g. webhook updating last_disabled_reason) is rare
  // while this view is open.
  useEffect(() => {
    if (data) setAutoForm(autoFormFromServer(data));
  }, [data]);

  // Reset confirmation any time the chosen amount changes — admin must re-aim.
  useEffect(() => {
    setConfirming(false);
  }, [selected, customRaw]);

  const customParsed = Number(customRaw);
  const customIsWhole =
    customRaw.trim() !== "" &&
    Number.isFinite(customParsed) &&
    Number.isInteger(customParsed);
  const customError: string | null = useMemo(() => {
    if (selected !== CUSTOM_OPTION) return null;
    if (customRaw.trim() === "") return "Enter an amount";
    if (!customIsWhole) return "Whole number required";
    if (customParsed < MIN_CUSTOM)
      return `Minimum ${MIN_CUSTOM.toLocaleString()} credits`;
    if (customParsed > MAX_CUSTOM)
      return `Maximum ${MAX_CUSTOM.toLocaleString()} credits`;
    return null;
  }, [selected, customRaw, customIsWhole, customParsed]);

  const credits: number | null =
    selected === CUSTOM_OPTION
      ? customError === null
        ? customParsed
        : null
      : selected;

  const unitAmountCents = data?.unit_amount_cents ?? 125;
  const totalCents = credits == null ? null : credits * unitAmountCents;

  const autoErrors = useMemo(() => validateAutoForm(autoForm), [autoForm]);
  const autoHasErrors = Object.keys(autoErrors).length > 0;

  // Dirty if the user has edited any auto field relative to the server snapshot.
  const autoDirty = useMemo(() => {
    if (!data) return false;
    return !autoFormEqual(autoFormFromServer(data), autoForm);
  }, [data, autoForm]);

  const inflight = isCharging || isSavingAuto;
  const hasDefaultPaymentMethod = data?.has_default_payment_method ?? false;
  const primaryDisabled =
    inflight ||
    credits == null ||
    !hasDefaultPaymentMethod ||
    (autoForm.enabled && autoHasErrors);

  const handlePrimary = useCallback(async () => {
    if (primaryDisabled || credits == null) return;
    if (!confirming) {
      setConfirming(true);
      return;
    }
    setChargeError(null);

    // Charge only — no merged auto-config save. The "Save Auto-refill
    // settings" inline button is the only path that persists preferences,
    // so admin intent is always explicit.
    try {
      const result = await manualCharge({
        credits,
        idempotencyKey:
          typeof crypto !== "undefined" && "randomUUID" in crypto
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random()}`,
      }).unwrap();
      if (result.status === "pending_authentication") {
        setChargeError(
          "Your card needs additional verification (3D Secure). Open the billing portal to complete the charge.",
        );
        setConfirming(false);
        return;
      }
      if (result.status !== "succeeded") {
        setChargeError(
          result.failureMessage ?? "The charge could not be completed.",
        );
        setConfirming(false);
        return;
      }
    } catch (err: unknown) {
      const errData = (err as { data?: { error?: { message?: string } } }).data;
      setChargeError(
        errData?.error?.message ?? "The charge could not be completed.",
      );
      setConfirming(false);
      return;
    }

    // Charge succeeded — top-placement toast confirms to the admin. The
    // toast persists 4.5s and lives at top:46 so it stays visible after
    // the modal hard-closes below.
    if (totalCents != null) {
      sendSuccessUINotification({
        message: `${credits.toLocaleString()} credits added`,
        description: `Charged ${formatUsd(totalCents)} to your card on file.`,
        placement: NotificationPosition.top,
      });
    }

    // Hard-close the entire Manage Plan modal — afterClose resets
    // modalView to "manage" so the next open lands on the plan view, not
    // on a stale topup view.
    onClose();
  }, [primaryDisabled, credits, confirming, totalCents, manualCharge, onClose]);

  const handleSaveAutorefill = useCallback(async () => {
    if (isSavingAuto || autoHasErrors || !autoDirty) return;
    try {
      await updateAutoConfig({
        enabled: autoForm.enabled,
        packCredits: isWholeNumber(autoForm.packCredits)
          ? Number(autoForm.packCredits)
          : null,
        monthlyCreditCap: isWholeNumber(autoForm.monthlyCreditCap)
          ? Number(autoForm.monthlyCreditCap)
          : null,
        triggerBufferCredits: isWholeNumber(autoForm.triggerBufferCredits)
          ? Number(autoForm.triggerBufferCredits)
          : null,
      }).unwrap();
      setAutoSavedAt(Date.now());
      sendSuccessUINotification({
        message: autoForm.enabled
          ? "Auto-refill updated"
          : "Auto-refill turned off",
        placement: NotificationPosition.top,
      });
    } catch {
      sendWarningUINotification({
        message: "Auto-refill settings could not be saved",
        description: "Please try again.",
        placement: NotificationPosition.top,
      });
    }
  }, [
    isSavingAuto,
    autoHasErrors,
    autoDirty,
    updateAutoConfig,
    autoForm.enabled,
    autoForm.packCredits,
    autoForm.monthlyCreditCap,
    autoForm.triggerBufferCredits,
  ]);

  // Auto-clear the "✓ Saved" green chip after 2s so the slot reverts to
  // its hidden state once the form is no longer dirty. Tied to the
  // timestamp so rapid re-saves restart the timer cleanly.
  useEffect(() => {
    if (autoSavedAt == null) return;
    const t = setTimeout(() => setAutoSavedAt(null), 2000);
    return () => clearTimeout(t);
  }, [autoSavedAt]);

  // The primary action commits the manual charge only; auto-refill settings
  // are saved independently via the inline "Save Auto-refill settings"
  // button, so the label never advertises a merged save.
  const primaryLabel = inflight
    ? `Refilling ${(credits ?? 0).toLocaleString()} credits…`
    : confirming && totalCents != null
      ? `Confirm and pay ${formatUsd(totalCents)} now`
      : credits != null
        ? `Refill ${credits.toLocaleString()} credits`
        : "Refill credits";

  return (
    <div
      data-test="manage-plan-topup-view"
      className="flex max-h-[90vh] flex-col overflow-hidden bg-white"
    >
      {/* Header — back chevron + title + close */}
      <div className="flex shrink-0 items-center gap-2 border-b border-[#e8eaed] px-6 py-4">
        <button
          type="button"
          aria-label="Back to plan view"
          onClick={onBack}
          disabled={inflight}
          className="-ml-1 flex h-7 w-7 cursor-pointer items-center justify-center rounded border-0 bg-transparent text-[#6c7689] transition hover:bg-[#f3f4f6] hover:text-[#1f2633] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="10,3 5,8 10,13" />
          </svg>
        </button>
        <h2 className="!m-0 flex-1 text-[15px] font-semibold leading-5 tracking-[-0.3px] text-[#1f2633]">
          Refill credits
        </h2>
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="flex cursor-pointer items-center justify-center rounded p-1 border-0 bg-transparent text-[#6c7689] transition hover:bg-[#f3f4f6]"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 4 4 12M4 4l8 8" />
          </svg>
        </button>
      </div>

      {/* Body */}
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-6">
        {isLoading || !data ? (
          <div className="text-xs text-[#6c7689]">Loading…</div>
        ) : (
          <>
            {/* ── Refill credits subsection ─────────────────────────── */}
            <div className="flex flex-col gap-1">
              <h3 className="!m-0 text-[14px] font-semibold leading-5 text-[#1f2633]">
                Refill credits
              </h3>
              <p className="m-0 text-xs leading-[1.45] text-[#6c7689]">
                Purchase a one-time refill. Credits are added to your shared
                pool immediately and expire at the end of the next billing
                cycle.
              </p>
            </div>

            <div className="flex flex-col gap-1">
              <span className="text-[11px] text-[#6c7689]">Amount</span>
              <Select<AmountOption>
                data-test="topup-view-amount"
                value={selected}
                onChange={(v) => setSelected(v)}
                disabled={inflight || confirming}
                options={[
                  ...PRESET_AMOUNTS.map((n) => ({
                    value: n,
                    label: `${n.toLocaleString()} credits`,
                  })),
                  { value: CUSTOM_OPTION, label: "Custom amount" },
                ]}
                style={{ width: "100%" }}
              />
            </div>

            {selected === CUSTOM_OPTION ? (
              <div className="flex items-center gap-2">
                <input
                  data-test="topup-view-custom-input"
                  type="number"
                  min={MIN_CUSTOM}
                  max={MAX_CUSTOM}
                  step={1}
                  value={customRaw}
                  onChange={(e) => setCustomRaw(e.target.value)}
                  onFocus={(e) => e.currentTarget.select()}
                  disabled={inflight || confirming}
                  autoFocus
                  className={`h-8 w-[130px] rounded border px-2.5 text-[13px] ${
                    customError ? "border-[#b91c1c]" : "border-[#e8eaed]"
                  }`}
                />
                <span
                  className={`text-[11px] ${
                    customError ? "text-[#b91c1c]" : "text-[#6c7689]"
                  }`}
                >
                  {customError
                    ? customError
                    : `credits · ${MIN_CUSTOM.toLocaleString()} – ${MAX_CUSTOM.toLocaleString()}`}
                </span>
              </div>
            ) : null}

            {/* Pricing card */}
            <div className="flex flex-col gap-1 rounded-md bg-[#f9fafb] px-3 py-2.5 text-xs">
              <PricingRow
                left="Unit price"
                right={`${formatUsd(unitAmountCents)} / credit`}
              />
              {credits != null ? (
                <PricingRow
                  left={`${credits.toLocaleString()} credits`}
                  right={formatUsd(credits * unitAmountCents)}
                />
              ) : null}
              {totalCents != null ? (
                <PricingRow
                  total
                  left="Total today"
                  right={formatUsd(totalCents)}
                />
              ) : null}
              <div className="mt-0.5 flex items-baseline gap-1.5">
                <span className="rounded-full bg-[#d1fae5] px-1.5 py-px text-[10px] font-semibold text-[#047857]">
                  Same rate
                </span>
                <span className="text-[11px] text-[#6c7689]">
                  as your <b>Org Credit Pack</b>.
                </span>
              </div>
            </div>

            <hr className="my-1 h-px border-0 border-t border-[#e8eaed]" />

            {/* ── Auto refill subsection ─────────────────────────────
                 Shared with /spending's AutoTopUpSettingsModal (single
                 source of truth for the field grid + inline pricing).
                 The inline Save button is TopUpView-specific so admins
                 can save without buying credits in the same click. */}
            <div className="flex flex-col gap-1">
              <h3 className="!m-0 text-[14px] font-semibold leading-5 text-[#1f2633]">
                Auto refill
              </h3>
              <p className="m-0 text-xs leading-[1.45] text-[#6c7689]">
                Keep your team building without interruption. Set up automatic
                credit refills when your balance hits a minimum threshold.
              </p>
            </div>

            <AutoTopUpFormFields
              form={autoForm}
              onFormChange={setAutoForm}
              errors={autoErrors}
              unitAmountCents={unitAmountCents}
              hasDefaultPaymentMethod={hasDefaultPaymentMethod}
              disabled={inflight || confirming}
              switchRowSlot={
                autoDirty || autoSavedAt != null ? (
                  <button
                    type="button"
                    data-test="topup-view-save-autorefill"
                    onClick={handleSaveAutorefill}
                    disabled={isSavingAuto || autoHasErrors || !autoDirty}
                    className={
                      autoSavedAt != null
                        ? "inline-flex h-7 items-center gap-1 rounded-md bg-[#d1fae5] px-3 text-xs font-medium text-[#047857]"
                        : "inline-flex h-7 cursor-pointer items-center justify-center rounded-md border-0 bg-white px-3 text-xs font-medium text-[#1f2633] shadow-[0_0_0_1px_rgba(31,38,51,0.12),0_1px_3px_rgba(31,38,51,0.12)] transition hover:bg-[#f9fafb] disabled:cursor-not-allowed disabled:opacity-50"
                    }
                  >
                    {autoSavedAt != null
                      ? "✓ Saved"
                      : isSavingAuto
                        ? "Saving…"
                        : "Save Auto-refill settings"}
                  </button>
                ) : undefined
              }
            />

            {chargeError ? (
              <div
                data-test="topup-view-charge-error"
                className="rounded-md bg-[#fde8e8] px-3 py-2 text-xs text-[#991b1b]"
              >
                {chargeError}
              </div>
            ) : null}

            {!hasDefaultPaymentMethod ? (
              <div className="rounded-md bg-[#dbeafe] px-3 py-2 text-xs text-[#1e3a8a]">
                Add a payment method in the billing portal before buying
                credits.
              </div>
            ) : null}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="flex shrink-0 items-center justify-end gap-2 border-t border-[#e8eaed] bg-[#f9fafb] px-6 py-3">
        {confirming && !inflight ? (
          <button
            type="button"
            onClick={() => setConfirming(false)}
            className="inline-flex h-9 cursor-pointer items-center justify-center rounded-md border border-[#e8eaed] bg-white px-3 text-sm font-medium text-[#454d5f] transition hover:bg-[#f9fafb]"
          >
            Cancel
          </button>
        ) : (
          <button
            type="button"
            onClick={onBack}
            disabled={inflight}
            className="inline-flex h-9 cursor-pointer items-center justify-center rounded-md border border-[#e8eaed] bg-white px-3 text-sm font-medium text-[#454d5f] transition hover:bg-[#f9fafb] disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
        )}
        <button
          type="button"
          data-test="topup-view-primary"
          onClick={handlePrimary}
          disabled={primaryDisabled}
          className="inline-flex h-9 cursor-pointer items-center justify-center rounded-md bg-black px-4 text-sm font-medium !text-white shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition hover:bg-black/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {primaryLabel}
        </button>
      </div>
    </div>
  );
}

function PricingRow({
  left,
  right,
  total = false,
}: {
  left: string;
  right: string;
  total?: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-between ${
        total
          ? "mt-0.5 border-t border-[#f1f3f5] pt-1.5 font-semibold text-[#1f2633]"
          : "text-[#6c7689]"
      }`}
    >
      <span>{left}</span>
      <span className="text-[#1f2633] tabular-nums">{right}</span>
    </div>
  );
}
